import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';

let instanceCounter = 0;

/**
 * Text Scramble Effect / Hacker Text Animation
 * 
 * Efek teks yang mengubah huruf per huruf dengan karakter acak.
 * Teks asli tidak terlihat di awal, baru muncul setelah animasi scramble selesai.
 */
const TextScramble = ({ 
  text, 
  texts,
  className = '', 
  duration = 2000, 
  delay = 0,
  triggerOnView = true,
  loop = false,
  loopInterval = 4000,
  rotate = false,
  rotateInterval = 4000,
  entranceAnimation = 'none'
}) => {
  const instanceId = useRef(++instanceCounter).current;
  
  const textArray = useMemo(() => texts || [text], [texts, text]);
  const [currentIndex, setCurrentIndex] = useState(0);
  // Mulai dengan string kosong, bukan teks asli
  const [displayText, setDisplayText] = useState('');
  const [isAnimating, setIsAnimating] = useState(false);
  const [hasFinished, setHasFinished] = useState(false);
  const [entranceComplete, setEntranceComplete] = useState(entranceAnimation === 'none');
  
  const elementRef = useRef(null);
  const frameIdRef = useRef(null);
  const hasStartedRef = useRef(false);
  const isVisibleRef = useRef(false);
  
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';

  const runScramble = useCallback((fromText, toText, onDone) => {
    if (frameIdRef.current) {
      cancelAnimationFrame(frameIdRef.current);
    }
    
    setIsAnimating(true);
    const targetLength = toText.length;
    // Generate random initial text dengan panjang yang sama
    let initialText = '';
    for (let i = 0; i < targetLength; i++) {
      initialText += chars[Math.floor(Math.random() * chars.length)];
    }
    
    setDisplayText(initialText);
    
    const totalFrames = Math.max(Math.floor(duration / 33), 30);
    let frame = 0;

    const animate = () => {
      frame++;
      const progress = frame / totalFrames;
      const lockedCount = Math.floor(progress * targetLength);
      
      let result = '';
      
      for (let i = 0; i < targetLength; i++) {
        if (i < lockedCount) {
          result += toText[i];
        } else {
          result += chars[Math.floor(Math.random() * chars.length)];
        }
      }
      
      setDisplayText(result);
      
      if (frame < totalFrames) {
        frameIdRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayText(toText);
        setIsAnimating(false);
        setHasFinished(true);
        if (onDone) onDone();
      }
    };
    
    frameIdRef.current = requestAnimationFrame(animate);
  }, [chars, duration]);

  // Entrance animation effect
  useEffect(() => {
    if (entranceAnimation === 'none') return;
    
    const timer = setTimeout(() => {
      setEntranceComplete(true);
    }, 600);
    
    return () => clearTimeout(timer);
  }, [entranceAnimation]);

  // Initial animation sequence
  useEffect(() => {
    const startScramble = () => {
      if (hasStartedRef.current) return;
      hasStartedRef.current = true;
      
      const totalDelay = entranceAnimation !== 'none' 
        ? delay + 600
        : delay;
      
      setTimeout(() => {
        runScramble('', textArray[0]);
      }, totalDelay);
    };

    if (!triggerOnView) {
      startScramble();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasStartedRef.current) {
            isVisibleRef.current = true;
            startScramble();
          } else if (!entry.isIntersecting) {
            isVisibleRef.current = false;
          }
        });
      },
      { threshold: 0.1 }
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      observer.disconnect();
      if (frameIdRef.current) {
        cancelAnimationFrame(frameIdRef.current);
      }
    };
  }, [delay, triggerOnView, entranceAnimation, textArray, runScramble]);

  // Rotate animation
  useEffect(() => {
    if (!rotate || textArray.length < 2) return;
    
    const interval = setInterval(() => {
      if (!isVisibleRef.current) return;
      
      const nextIndex = (currentIndex + 1) % textArray.length;
      runScramble(textArray[currentIndex], textArray[nextIndex], () => {
        setCurrentIndex(nextIndex);
      });
    }, rotateInterval);

    return () => clearInterval(interval);
  }, [rotate, rotateInterval, currentIndex, textArray, runScramble]);

  // Loop animation
  useEffect(() => {
    if (!loop || textArray.length > 1) return;
    
    const interval = setInterval(() => {
      if (hasFinished && isVisibleRef.current) {
        runScramble(textArray[0], textArray[0]);
      }
    }, loopInterval);

    return () => clearInterval(interval);
  }, [loop, loopInterval, hasFinished, textArray, runScramble]);

  useEffect(() => {
    return () => {
      if (frameIdRef.current) {
        cancelAnimationFrame(frameIdRef.current);
      }
    };
  }, []);

  // Entrance animation styles
  const getEntranceStyles = () => {
    if (entranceAnimation === 'none') return { opacity: 1 };
    
    if (!entranceComplete) {
      return {
        opacity: 0,
        transform: 'translateY(20px)',
        transition: 'opacity 0.5s ease, transform 0.5s ease'
      };
    }
    
    return {
      opacity: 1,
      transform: 'translateY(0)',
      transition: 'opacity 0.5s ease, transform 0.5s ease'
    };
  };

  return (
    <span 
      ref={elementRef} 
      className={`text-scramble ${className} ${isAnimating ? 'scrambling' : ''}`}
      style={{ 
        whiteSpace: 'nowrap',
        display: 'inline-block',
        minWidth: '1ch', // Minimal 1 character width agar tidak collapse
        ...getEntranceStyles()
      }}
      data-instance={instanceId}
    >
      {displayText}
    </span>
  );
};

export const MultiLineTextScramble = ({
  lines,
  className = '',
  lineClassName = '',
  baseDelay = 0,
  staggerDelay = 400,
  duration = 2000,
  ...props
}) => {
  return (
    <span className={className}>
      {lines.map((line, index) => (
        <span key={index} className={lineClassName}>
          <TextScramble
            text={line}
            delay={baseDelay + (index * staggerDelay)}
            duration={duration}
            className="text-scramble-line"
            {...props}
          />
          {index < lines.length - 1 && <br />}
        </span>
      ))}
    </span>
  );
};

export default TextScramble;
